// Copyright (C) Microsoft Corporation. All rights reserved.
import diff from 'diff-sequences';
import { NodeId, TraitLocation, TreeView } from '@fluid-experimental/tree';

export interface WithId {
  id: NodeId;
}

export interface WithSource {
  source: TraitLocation;
}

export interface WithDestination {
  destination: TraitLocation;
}

export type DeltaMap = Map<NodeId, Partial<WithSource & WithDestination>>;

/** Calculate delta based on moved elements between two forests. */
export function deltaMove(before: TreeView, after: TreeView): DeltaMap {
  const delta = before.delta(after);

  const moved: DeltaMap = new Map();

  for (const id of delta.changed) {
    // Ignoring changed values: only looking at children as thats all Delta2 requires.
    const beforeNode = before.getViewNode(id);
    const afterNode = after.getViewNode(id);
    const traits = new Set(beforeNode.traits.keys());
    afterNode.traits.forEach((_, label) => traits.add(label));
    for (const label of traits) {
      const beforeContent = beforeNode.traits.get(label) ?? [];
      const afterContent = afterNode.traits.get(label) ?? [];
      if (beforeContent !== afterContent) {
        const beforeLength = beforeContent.length;
        const afterLength = afterContent.length;
        const isCommon = (beforeIndex: number, afterIndex: number): boolean =>
          beforeContent[beforeIndex] === afterContent[afterIndex];
        let beforeIndex = 0;
        let afterIndex = 0;
        const foundSubsequence = (nCommon: number, beforeCommon: number, afterCommon: number): void => {
          for (; beforeIndex !== beforeCommon; beforeIndex += 1) {
            const removedChild = beforeContent[beforeIndex];
            getOrAddInMap(moved, removedChild, {}).source = { parent: id, label };
          }
          for (; afterIndex !== afterCommon; afterIndex += 1) {
            const addedChild = afterContent[afterIndex];
            getOrAddInMap(moved, addedChild, {}).destination = { parent: id, label };
          }
          // We don't care about common children (they are unchanged), so skip over them.
          beforeIndex += nCommon;
          afterIndex += nCommon;
        };
        diff(beforeLength, afterLength, isCommon, foundSubsequence);
        // After the last common subsequence, handle remaining children
        foundSubsequence(0, beforeLength, afterLength);
      }
    }
  }
  return moved;
}

/**
 * Sets the value at (key1, key2) in map to value if not already present. Returns the value at (key1, key2) after
 * setting it. This is equivalent to a get or default that adds the default to the map.
 */
function getOrAddInMap<Key, Value>(map: Map<Key, Value>, key: Key, value: Value): Value {
  const currentValue = map.get(key);
  if (currentValue !== undefined) {
    return currentValue;
  }
  map.set(key, value);
  return value;
}
